import { useState, type FormEvent } from 'react';
import type { Environment, Project } from '@hangar/shared';
import { toast } from 'sonner';
import { useEnvironmentMutations } from '@/api/hooks';
import { showErrorToast } from '@/lib/toasts';
import { failureEffectOf } from './environmentFailure';
import { addedToast, buildCreateBody, buildPatchBody, deriveEnvView, firstInvalidField, initialEnvValues, savedToast, shownError, type EnvFormValues, type EnvView, type Field, type FieldErrors } from './environmentFormModel';

export interface EnvFormOptions {
  project: Project;
  /** Absent: a new environment. */
  environment?: Environment;
  onDone: (saved?: Environment) => void;
}

/** The create and patch calls, each with its toast; a patch with nothing changed sends nothing and resolves `undefined`. */
function useEnvSave({ project, environment: env }: EnvFormOptions) {
  const { create, update } = useEnvironmentMutations(project.id);
  const createEnv = async (v: EnvFormValues, view: EnvView) => {
    const saved = await create.mutateAsync(buildCreateBody(v, view));
    const { title, description } = addedToast(saved);
    toast.success(title, { description });
    return saved;
  };
  const patchEnv = async (existing: Environment, v: EnvFormValues, view: EnvView) => {
    const body = buildPatchBody(existing, v, view);
    if (Object.keys(body).length === 0) return undefined;
    const saved = await update.mutateAsync({ environmentId: existing.id, body, etag: existing.etag ?? project.etag });
    const { title, description } = savedToast(saved);
    toast.success(title, { description });
    return saved;
  };
  return { save: (v: EnvFormValues, view: EnvView) => (env ? patchEnv(env, v, view) : createEnv(v, view)), busy: create.isPending || update.isPending };
}

/** What the last submit left on screen: whether it ran, the API's field messages and the problem alert. */
function useEnvFailureState() {
  const [submitted, setSubmitted] = useState(false);
  const [server, setServer] = useState<FieldErrors>({});
  const [problem, setProblem] = useState<unknown>(null);
  return {
    submitted,
    server,
    problem,
    begin: () => {
      setSubmitted(true);
      setServer({});
      setProblem(null);
    },
    /** Editing a field drops the API's message about it. */
    clear: (field: Field) => setServer((s) => (s[field] ? { ...s, [field]: undefined } : s)),
    fail: (err: unknown, slugChanged: boolean) => {
      const effect = failureEffectOf(err, slugChanged);
      if (effect.server) setServer(effect.server);
      if (effect.problem !== undefined) setProblem(effect.problem);
      if (effect.toast) showErrorToast(effect.toast);
    },
  };
}

/** State, derived view and submit of the environment form. */
export function useEnvironmentForm(options: EnvFormOptions) {
  const { project, environment: env, onDone } = options;
  const [values, setValues] = useState(() => initialEnvValues(project, env));
  const view = deriveEnvView(values);
  const state = useEnvFailureState();
  const { save, busy } = useEnvSave(options);
  const edit = (change: (v: EnvFormValues) => EnvFormValues, clears?: Field) => {
    setValues(change);
    if (clears) state.clear(clears);
  };
  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    state.begin();
    const first = firstInvalidField(view.local);
    if (first) return document.getElementById(`env-form-${first}`)?.focus();
    try {
      const saved = await save(values, view);
      if (saved) setValues((v) => ({ ...v, draft: '' }));
      onDone(saved);
    } catch (err) {
      state.fail(err, values.slug !== env?.slug);
    }
  };
  return { env, values, view, edit, busy, problem: state.problem, errorFor: (f: Field) => shownError(state.server, view.local, state.submitted, f), onSubmit, onCancel: () => onDone() };
}

export type EnvironmentFormApi = ReturnType<typeof useEnvironmentForm>;
